import type { Backend } from "../api/backend";
import { ApiError } from "../api/http";
import type {
  Familiarity,
  RadioRun,
  RadioStartJob,
  RadioStartProgressEntry,
  Segment,
  Talkativeness,
} from "../api/types";
import { deviceID, storage } from "../lib/storage";
import { Store } from "../lib/store";
import { delay, localISOTime } from "../lib/time";
import type { AppleMusic } from "../music/appleMusic";
import { type Block, materialize, type TrackResolver } from "../playback/blocks";
import type { PlaybackEngine } from "../playback/engine";
import { RunFollower } from "./runFollower";

export interface StationOptions {
  brief: string;
  language: string;
  talkativeness: Talkativeness;
  familiarity: Familiarity;
  voiceProfileID: string;
}

export type StationPhase = "idle" | "starting" | "onAir" | "failed";

export interface StationState {
  phase: StationPhase;
  playlistID: string | null;
  title: string;
  stage: string | null;
  progress: RadioStartProgressEntry[];
  /** The run as the server sees it: `on_air`, `paused` or `stopped`. */
  runStatus: string | null;
  directives: string[];
  error: string | null;
  /** What the listener should know about this station, e.g. that the host is off. */
  notices: string[];
  /** A station still on air from before this page loaded, waiting for a click. */
  resumable: string | null;
}

const ACTIVE_STATION_KEY = "radiola.active-station";
const JOB_POLL_MS = 1_500;

/**
 * A station the server keeps on air, played in this browser: the web's version of the
 * phone's durable start (`RadioStartJobService`) and server-run follow path
 * (`AgentShowGeneratorService.startServerFollowing`).
 *
 * Start: admission → start job → poll until the build is ready → the run's segments
 * become blocks → the engine plays them and the follower appends what the server adds.
 */
export class Station extends Store<StationState> {
  private follower: RunFollower | null = null;
  private startAbort: AbortController | null = null;
  private jobID: string | null = null;
  /** Server positions already turned into blocks (or rejected), so none is done twice. */
  private seenPositions = new Set<number>();

  constructor(
    private readonly backend: Backend,
    private readonly engine: PlaybackEngine,
    private readonly music: AppleMusic,
    private readonly resolveTrack: TrackResolver,
  ) {
    super({
      phase: "idle",
      playlistID: null,
      title: "",
      stage: null,
      progress: [],
      runStatus: null,
      directives: [],
      error: null,
      notices: [],
      resumable: null,
    });
  }

  async start(options: StationOptions): Promise<void> {
    if (this.state.phase === "starting") return;
    this.leave();
    const abort = new AbortController();
    this.startAbort = abort;
    this.update({
      phase: "starting",
      playlistID: null,
      title: "",
      stage: "admission",
      progress: [],
      runStatus: null,
      directives: [],
      error: null,
      notices: [],
      resumable: null,
    });
    try {
      const capabilities = await this.backend.radioCapabilities();
      if (!capabilities.durable_start_jobs) {
        throw new StationError("The server can't build stations right now.");
      }
      const admission = await this.backend.admitRadioStart(options.voiceProfileID);
      if (admission.missing_permissions.length > 0) {
        throw new StationError(
          "This voice needs AI permissions accepted in the iPhone app first (Settings → Privacy).",
        );
      }
      const library = await this.music.librarySnapshot().catch(() => []);
      abort.signal.throwIfAborted();

      const playlistID = crypto.randomUUID();
      const job = await this.backend.startRadio({
        version: 1,
        playlist_id: playlistID,
        device_id: deviceID(),
        idempotency_key: crypto.randomUUID(),
        brief: options.brief,
        language: options.language,
        talkativeness: options.talkativeness,
        familiarity: options.familiarity,
        directives: [],
        voice_profile_id: options.voiceProfileID,
        visibility: "private",
        listener_place: "",
        local_time: localISOTime(),
        storefront: this.music.storefront,
        library,
      });
      this.jobID = job.id;
      storage.set(ACTIVE_STATION_KEY, playlistID);
      this.update({ playlistID });

      const ready = await this.awaitBuild(job, abort.signal);
      const manifest = ready.result;
      if (!manifest) throw new StationError("The server finished without a station.");
      const notices = manifest.warnings.map((warning) => warning.message);
      if (manifest.playback_mode === "music_only") {
        notices.unshift("Music only for now: the host joins with the next batch.");
      }
      this.update({ notices });
      await this.goOnAir(playlistID, manifest.title, manifest.segments);
    } catch (error) {
      if (abort.signal.aborted) return;
      storage.remove(ACTIVE_STATION_KEY);
      this.update({ phase: "failed", error: describe(error) });
    } finally {
      if (this.startAbort === abort) this.startAbort = null;
      this.jobID = null;
    }
  }

  async cancelStart(): Promise<void> {
    const jobID = this.jobID;
    this.startAbort?.abort();
    this.startAbort = null;
    storage.remove(ACTIVE_STATION_KEY);
    this.update({ phase: "idle", stage: null, progress: [], playlistID: null });
    if (jobID) await this.backend.cancelStartJob(jobID).catch(() => undefined);
  }

  /** Corrections the host takes into the next batch; the unplayed tail is rebuilt. */
  async steer(directive: string): Promise<void> {
    const text = directive.trim();
    const playlistID = this.state.playlistID;
    if (!text || !playlistID) return;
    if (this.state.directives.includes(text)) return;
    const directives = [...this.state.directives, text].slice(-12);
    await this.sendDirectives(playlistID, directives);
  }

  async removeDirective(index: number): Promise<void> {
    const playlistID = this.state.playlistID;
    if (!playlistID) return;
    await this.sendDirectives(
      playlistID,
      this.state.directives.filter((_, position) => position !== index),
    );
  }

  /** Stops the server building more; what is queued keeps playing. */
  async pauseGeneration(): Promise<void> {
    const playlistID = this.state.playlistID;
    if (!playlistID) return;
    const run = await this.backend.pauseRun(playlistID);
    this.update({ runStatus: run.status });
  }

  async resumeGeneration(): Promise<void> {
    const playlistID = this.state.playlistID;
    if (!playlistID) return;
    const run = await this.backend.resumeRun(playlistID);
    this.update({ runStatus: run.status });
  }

  async stop(): Promise<void> {
    const playlistID = this.state.playlistID;
    this.leave();
    this.engine.stop();
    if (playlistID) await this.backend.stopRun(playlistID).catch(() => undefined);
  }

  /** Stops following without ending the run: the listener went to play something else. */
  leave(): void {
    this.follower?.detach();
    this.follower = null;
    this.engine.onAdvance = null;
    this.engine.onTrackFailed = null;
    storage.remove(ACTIVE_STATION_KEY);
    this.update({ phase: "idle", runStatus: null, playlistID: null, resumable: null });
  }

  /** A station left on air by an earlier visit: offered, not autoplayed. */
  async findResumable(): Promise<void> {
    const playlistID = storage.get(ACTIVE_STATION_KEY);
    if (!playlistID) return;
    try {
      const run = await this.backend.run(playlistID);
      if (run.status === "on_air" || run.status === "paused") {
        this.update({ resumable: playlistID });
        return;
      }
    } catch {
      // No run any more.
    }
    storage.remove(ACTIVE_STATION_KEY);
  }

  async resume(): Promise<void> {
    const playlistID = this.state.resumable;
    if (!playlistID) return;
    this.update({ resumable: null, error: null });
    try {
      const run = await this.backend.run(playlistID);
      const title = await this.backend
        .playlist(playlistID)
        .then((playlist) => playlist.title)
        .catch(() => "Your station");
      await this.goOnAir(playlistID, title, run.segments, run);
    } catch (error) {
      this.update({ phase: "failed", error: describe(error) });
    }
  }

  // MARK: - Private

  private async awaitBuild(job: RadioStartJob, signal: AbortSignal): Promise<RadioStartJob> {
    let current = job;
    for (;;) {
      this.update({ stage: current.stage, progress: current.progress });
      if (current.status === "ready") return current;
      if (current.status === "failed") throw new StationError(failureMessage(current.error_code));
      if (current.status === "cancelled") throw new StationError("The station was cancelled.");
      await delay(JOB_POLL_MS, signal);
      current = await this.backend.startJob(job.id);
    }
  }

  private async goOnAir(
    playlistID: string,
    title: string,
    starterSegments: Segment[],
    knownRun?: RadioRun,
  ): Promise<void> {
    const run = knownRun ?? (await this.backend.run(playlistID).catch(() => null));
    this.seenPositions = new Set();
    const blocks = await this.blocksFor(playlistID, run?.segments ?? starterSegments);
    if (blocks.length === 0) {
      throw new StationError("None of this station's tracks play in your Apple Music storefront.");
    }
    const isLive = run !== null && run.status !== "stopped";
    storage.set(ACTIVE_STATION_KEY, playlistID);
    this.update({
      phase: "onAir",
      playlistID,
      title,
      stage: null,
      runStatus: run?.status ?? "stopped",
      directives: run?.directives ?? [],
    });

    const follower = isLive && run ? this.follow(playlistID, run.last_seq) : null;
    this.engine.onAdvance = (block) => {
      if (block.position !== null) follower?.reportCursor(block.position);
    };
    this.engine.onTrackFailed = (block) => {
      if (block.position === null) return;
      void this.backend
        .reportUnplayable(playlistID, block.position, block.candidateIDs)
        .catch(() => undefined);
    };
    const cursor = run?.cursor ?? null;
    const startIndex =
      cursor === null
        ? 0
        : Math.max(
            0,
            blocks.findIndex((block) => block.position !== null && block.position >= cursor),
          );
    const loading = this.engine.load({ id: playlistID, title, blocks, isLive }, startIndex);
    follower?.start();
    await loading;
  }

  private follow(playlistID: string, afterSeq: number): RunFollower {
    this.follower?.detach();
    const follower = new RunFollower(this.backend, playlistID, afterSeq, {
      onSegments: (segments, truncateFrom) =>
        void this.adopt(follower, playlistID, segments, truncateFrom),
      onStatus: (status) => this.adoptStatus(follower, status),
      cursor: () => {
        const position = this.engine.currentBlock?.position;
        return position === null || position === undefined
          ? null
          : { position, offsetSeconds: this.engine.state.position };
      },
    });
    this.follower = follower;
    return follower;
  }

  private async adopt(
    follower: RunFollower,
    playlistID: string,
    segments: Segment[],
    truncateFrom: number | null,
  ): Promise<void> {
    if (this.follower !== follower) return;
    if (truncateFrom !== null) {
      this.engine.truncate(truncateFrom);
      for (const position of this.seenPositions) {
        if (position >= truncateFrom) this.seenPositions.delete(position);
      }
    }
    const fresh = segments.filter((segment) => !this.seenPositions.has(segment.position));
    const blocks = await this.blocksFor(playlistID, fresh);
    if (this.follower === follower) this.engine.append(blocks);
  }

  private adoptStatus(follower: RunFollower, status: string): void {
    if (this.follower !== follower) return;
    this.update({ runStatus: status });
    if (status === "stopped") {
      follower.detach();
      this.follower = null;
      this.engine.finishLive();
      storage.remove(ACTIVE_STATION_KEY);
    }
  }

  private async blocksFor(playlistID: string, segments: Segment[]): Promise<Block[]> {
    for (const segment of segments) this.seenPositions.add(segment.position);
    const { blocks, rejected } = await materialize(segments, this.resolveTrack);
    for (const { position, ids } of rejected) {
      void this.backend.reportUnplayable(playlistID, position, ids).catch(() => undefined);
    }
    return blocks;
  }

  private async sendDirectives(playlistID: string, directives: string[]): Promise<void> {
    try {
      await this.backend.setDirectives(playlistID, directives, true);
      this.update({ directives, error: null });
    } catch (error) {
      this.update({ error: describe(error) });
    }
  }
}

class StationError extends Error {}

function failureMessage(code: string | null): string {
  switch (code) {
    case "billing_denied":
      return "Out of credits: the AI host needs a plan with credits left.";
    case "planning_failed":
      return "The producer couldn't plan this station. Try another brief.";
    case "music_unavailable":
      return "No music matched the brief in your catalog.";
    case "voice_unavailable":
      return "The host's voice is unavailable right now. Pick another voice.";
    case "provider_unavailable":
      return "An AI provider is down right now. Try again in a minute.";
    default:
      return "The station couldn't be built. Try again.";
  }
}

function describe(error: unknown): string {
  if (error instanceof StationError) return error.message;
  if (error instanceof ApiError) {
    if (error.detail === "radio_start_already_active") {
      return "A station is already being built for this account.";
    }
    if (error.status === 402 || error.detail.includes("subscription")) {
      return "The AI host needs an active plan.";
    }
    return `The server said no (${error.status} ${error.detail}).`;
  }
  return error instanceof Error ? error.message : "Something went wrong.";
}
